import React, { useState, useEffect } from "react";
import { MODULE_API } from "../../apis";
import BUDGET_API from "../../apis/budget.api";
import DEATH_API from "../../apis/death.api";
import { Line } from "react-chartjs-2";
import { useTheme } from "../../utils/ThemeContext";
import moment from "moment";
import NewLoader from "../styled-components/loader/NewLoader";
import { Select } from "antd";
import "./detail-chart.css";
const { Option } = Select;

export const IncomeAndExpensChart = () => {
  const { theme } = useTheme();
  const [loading, setLoading] = useState(false);
  const [incomeChartData, setIncomeChartData] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [expenseLabel, setExpenseLabel] = useState(null);
  const [clientTransactionsCategories, setClientTransactionsCategories] =
    useState(null);
  const [clientTransactionsSubCategories, setClientTransactionsSubCategories] =
    useState(null);
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const getIncomeChartData = async (type) => {
    try {
      setLoading(true);
      const currentDate = moment();
      const startDate = currentDate
        .clone()
        .subtract(1, "year")
        .format("YYYY-MM-DD");

      const endDate = currentDate.clone().format("YYYY-MM-DD");
      let datares = await MODULE_API.fetchIncomeChartData(
        startDate,
        endDate,
        type
      );
      if (datares && datares.data) {
        const generatedChartData = generateIncomeChartData(
          datares.data,
          startDate,
          endDate
        );
        setIncomeChartData(generatedChartData);
      }
      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  const generateIncomeChartData = (incomeData, startDate, endDate) => {
    const momentStartDate = moment(startDate);
    const momentEndDate = moment(endDate);

    // Generate month labels & map for quick lookup
    const monthLabels = [];
    const monthIndexMap = {};
    let currentMonth = momentStartDate.clone();
    let idx = 0;
    while (currentMonth.isSameOrBefore(momentEndDate)) {
      const label = currentMonth.format("MMM YYYY");
      monthLabels.push(label);
      monthIndexMap[label] = idx++;
      currentMonth.add(1, "month");
    }

    // Initialize total income array
    const totalIncomePerMonth = Array(monthLabels.length).fill(0);

    const frequencyMultipliers = {
      Annually: 12,
      "Bi-Monthly": 2,
      "Bi-Weekly": 0.5,
      Monthly: 1,
      "One-Time": 0,
      Quarterly: 3,
      "Semi-Annually": 6,
      Weekly: 0.25,
    };

    incomeData.forEach((item) => {
      const incomeStart = moment(item.income_start_date);
      const incomeEnd = moment(item.income_end_date);
      const frequency = item.frequency || "Monthly";
      const monthlyIncome = item.gross_monthly_wages;

      if (!frequencyMultipliers[frequency]) return;

      if (
        incomeEnd.isBefore(momentStartDate) ||
        incomeStart.isAfter(momentEndDate)
      )
        return;

      const effectiveStart = moment.max(incomeStart, momentStartDate);
      const effectiveEnd = moment.min(incomeEnd, momentEndDate);
      const interval = frequencyMultipliers[frequency];

      for (
        let date = effectiveStart.clone();
        date.isSameOrBefore(effectiveEnd);
        date.add(1, "month")
      ) {
        const monthKey = date.format("MMM YYYY");
        const monthIndex = monthIndexMap[monthKey];
        if (monthIndex === undefined) continue;

        if (
          interval === 0 &&
          frequency === "One-Time" &&
          monthKey === effectiveStart.format("MMM YYYY")
        ) {
          totalIncomePerMonth[monthIndex] += monthlyIncome;
        } else if (interval <= 1) {
          const occurrences =
            frequency === "Weekly" ? 4 : frequency !== "Monthly" ? 2 : 1;
          totalIncomePerMonth[monthIndex] += monthlyIncome * occurrences;
        } else if (
          (monthIndex - monthIndexMap[effectiveStart.format("MMM YYYY")]) %
            interval ===
          0
        ) {
          totalIncomePerMonth[monthIndex] += monthlyIncome;
        }
      }
    });

    return {
      labels: monthLabels,
      data: totalIncomePerMonth,
      datasets: [
        {
          label: "Total Income",
          type: "bar",
          data: totalIncomePerMonth,
          backgroundColor: "#11C4C1",
          borderColor: "#11C4C1",
          stack: "stack1",
        },
      ],
    };
  };

  const getChartDataForLast12Months = async (payloadData, type) => {
    try {
      setLoading(true);
      const dateRanges = Array.from({ length: 13 }, (_, i) => {
        const startDate = moment()
          .subtract(i, "months")
          .startOf("month")
          .format("YYYY-MM-DD");
        const date = moment().subtract(i, "months").format("YYYY-MM");
        const endDate = moment()
          .subtract(i, "months")
          .endOf("month")
          .format("YYYY-MM-DD");
        return { month: date, startDate, endDate };
      });
      const categoryIds =
        clientTransactionsCategories &&
        clientTransactionsCategories
          .filter((item) => item.enable === true)
          .map((item) => item.categoryId);
      const subCategoryIds =
        clientTransactionsSubCategories &&
        clientTransactionsSubCategories
          .filter((item) => item.enable === true)
          .map((item) => item.subCategoryId);
      let payload = {
        categoryId:
          payloadData && payloadData.categoryIds
            ? payloadData.categoryIds
            : (categoryIds && categoryIds) || [],
        subCategoryId:
          payloadData && payloadData.subCategoryIds
            ? payloadData.subCategoryIds
            : (subCategoryIds && subCategoryIds) || [],
      };

      const apiCalls = dateRanges.map(({ startDate, endDate, month }) =>
        DEATH_API.getBudgetChartDataMonthly(startDate, endDate, {
          ...payload,
          startDate,
          endDate,
          creditCard: type,
        })
          .then((data) => ({ startDate, endDate, data, month })) // Add metadata to the result
          .catch((error) => {
            console.log(
              `Error fetching data for ${startDate} to ${endDate}:`,
              error
            );
            return { month, data: null };
          })
      );

      const chartDataArray = await Promise.all(apiCalls);
      const validData = dateRanges.map(({ month }) => {
        const entry = chartDataArray.find((e) => e.month === month).data;
        return entry
          ? { ...entry, month }
          : { month, total_expenses: "0.00", total_income: "0.00" };
      });
      const filteredData = chartDataArray.filter((entry) => entry.data);
      // const validData = chartDataArray
      //   .filter((entry) => entry.data && entry.data) // Filter successful responses
      //   .map((entry) => entry.data);
      console.log("chartDataArray", validData);
      if (validData) {
        const reversedChartData = validData ? [...validData].reverse() : [];
        const expensesData = reversedChartData
          ? reversedChartData.map((item) =>
              Math.abs(parseFloat(item.total_expenses))
            )
          : [];
        const chartLabel = reversedChartData
          ? reversedChartData.map((item) => {
              const [year, month] = item.month.split("-");
              return `${monthNames[parseInt(month) - 1]} ${year}`; // Format as "Month Year"
            })
          : [];
        setExpenseLabel(chartLabel);
        setChartData(expensesData);
      }
      setLoading(false);
    } catch (error) {
      console.log("Error fetching chart data:", error);
      setLoading(false);
    }
  };
  const getTransactionsCategories = async () => {
    try {
      setLoading(true);
      const date = moment();
      const startDate = date.startOf("month").format("YYYY-MM-DD"); // Start of the month
      const endDate = date.endOf("month").format("YYYY-MM-DD");
      let data = await BUDGET_API.fetchAllTransactionCatgoires();
      if (data && data.status === 200) {
        let categoryIds =
          data.data &&
          data.data
            .filter((item) => item.enable === true)
            .map((item) => item.categoryId);
        setClientTransactionsCategories(data.data);
        return categoryIds;
      }

      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };
  const getTransactionsSubCategories = async () => {
    try {
      setLoading(true);
      let data = await BUDGET_API.fetchAllTransactionSubCatgoires();
      if (data && data.status === 200) {
        let categoryIds =
          data.data &&
          data.data
            .filter((item) => item.enable === true)
            .map((item) => item.subCategoryId);
        setClientTransactionsSubCategories(data.data);

        return categoryIds;
      }

      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };
  const data = {
    labels:
      incomeChartData && incomeChartData.data
        ? incomeChartData.labels
        : expenseLabel
        ? expenseLabel
        : [],
    datasets: [
      {
        label: `Income $${
          incomeChartData && incomeChartData.data
            ? incomeChartData.data
                .reduce((sum, value) => sum + value, 0)
                .toLocaleString("en-US", { minimumFractionDigits: 2 })
            : "0.00"
        }`,
        data:
          incomeChartData && incomeChartData.data ? incomeChartData.data : [],
        fill: true,
        backgroundColor: "#4fff8da1",
        borderColor: "#4fff8da1",
        borderWidth: 0,
        pointRadius: 0,
        pointHoverRadius: 0,
        lineTension: 0, // Makes lines straight
        borderJoinStyle: "miter", // Sharp corners
      },
      {
        label: `Expenses $${
          chartData && chartData
            ? chartData
                .reduce((sum, value) => sum + value, 0)
                .toLocaleString("en-US", { minimumFractionDigits: 2 })
            : "0.00"
        }`,
        data: chartData ? chartData : [],
        fill: true,
        backgroundColor: "#6c4fff8a",
        borderColor: "#6c4fff8a",
        borderWidth: 0,
        pointRadius: 0,
        pointHoverRadius: 0,
        lineTension: 0, // Makes lines straight
        borderJoinStyle: "miter", // Sharp corners
      },
    ],
  };
  // const getAll = async () => {
  //   await getIncomeChartData();
  //   let categoryIds = await getTransactionsCategories();
  //   let subCategoryIds = await getTransactionsSubCategories();
  //   let payload = {
  //     categoryIds,
  //     subCategoryIds,
  //   };
  //   await getChartDataForLast12Months(payload);
  // };
  const getAll = async () => {
    try {
      setLoading(true);
      const [categoryIds, subCategoryIds, incomeData] = await Promise.all([
        getTransactionsCategories(),
        getTransactionsSubCategories(),
        getIncomeChartData(),
      ]);

      const payload = { categoryIds, subCategoryIds };
      await getChartDataForLast12Months(payload);
    } catch (error) {
      console.log("Error in getAll:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getAll();
  }, []);

  const incomeTypes = ["All", "Active", "Passive"];
  const accountTypes = ["All", "Credit Card"];

  return (
    <div
      className="chartjs-render-monitor-container"
      style={{
        backgroundColor: theme === "dark" ? "#232323" : "white",
        position: "relative",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px",
        }}
      >
        <h3
          style={{
            color: theme === "light" ? "#232323" : "white",
          }}
        >
          Income And Expenses
        </h3>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div className="filterIcons" style={{ flexDirection: "column" }}>
            <label>Select Income Source</label>

            <Select
              showSearch
              style={{ width: "100%", outline: "none", border: "none" }}
              placeholder="Select Income Source"
              defaultValue={"All"}
              onChange={(value, id) => {
                console.log("value", value);
                if (value === "All") {
                  getIncomeChartData();
                } else {
                  getIncomeChartData(value);
                }
              }}
            >
              {incomeTypes &&
                incomeTypes.map((item, index) => (
                  <Option key={index} value={item}>
                    {item}
                  </Option>
                ))}
            </Select>
          </div>
          <div className="filterIcons" style={{ flexDirection: "column" }}>
            <label>Select Expense Source</label>
            <Select
              showSearch
              style={{ width: "100%", outline: "none", border: "none" }}
              placeholder="Select Expense Source"
              defaultValue={"All"}
              onChange={(value, id) => {
                console.log("value", value);
                const categoryIds =
                  clientTransactionsCategories &&
                  clientTransactionsCategories
                    .filter((item) => item.enable === true)
                    .map((item) => item.categoryId);
                const subCategoryIds =
                  clientTransactionsSubCategories &&
                  clientTransactionsSubCategories
                    .filter((item) => item.enable === true)
                    .map((item) => item.subCategoryId);
                let payload = {
                  categoryIds,
                  subCategoryIds,
                };
                if (value === "Credit Card") {
                  getChartDataForLast12Months(payload, true);
                } else {
                  getChartDataForLast12Months(payload, false);
                }
              }}
            >
              {accountTypes &&
                accountTypes.map((item, index) => (
                  <Option key={index} value={item}>
                    {item}
                  </Option>
                ))}
            </Select>
          </div>
        </div>
      </div>
      <div style={{ marginTop: "1rem" }}>
        <Line
          data={data}
          options={{
            legend: {
              display: true,
              labels: {
                fontColor: theme === "light" ? "#232323" : "white",
              },
            },
            scales: {
              xAxes: [
                {
                  gridLines: {
                    color: "rgba(0, 0, 0, 0)",
                  },
                  ticks: {
                    fontColor: theme === "light" ? "#232323" : "white",
                  },
                },
              ],
              yAxes: [
                {
                  gridLines: {
                    color: "rgba(0, 0, 0, 0)",
                  },
                  ticks: {
                    fontColor: theme === "light" ? "#232323" : "white",
                    beginAtZero: true,
                    callback: function (value) {
                      return new Intl.NumberFormat("en-US", {
                        style: "currency",
                        currency: "USD",
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }).format(value);
                    },
                  },
                },
              ],
            },
            plugins: {},
          }}
        />
      </div>
      <NewLoader isLoading={loading} />
    </div>
  );
};
